<template>
  <div class="m-2">
    <div v-if="error" class="tw-bg-red-100 tw-rounded tw-p-2 tw-whitespace-pre-line">
      {{ error }}
    </div>
    <div v-if="batchSuccess" class="tw-bg-green-100 tw-rounded tw-p-2">
      {{ batchSuccess }}
    </div>

    <div class="tw-my-2 tw-flex tw-flex-col">
      <div class="tw-font-bold">Filter</div>

      <div class="tw-flex tw-flex-row tw-gap-2">
        <div class="tw-w-1/4">
          <label class="tw-mr-2 tw-uppercase tw-tracking-wider tw-text-xs">Groups</label>
          <MultiSelect
            v-if="groups.length > 0"
            v-model="filter.groups"
            :options="groups.map((g) => ({ value: g.id, label: `${g.project} / ${g.name}` }))"
            placeholder="Alle groepen"
            searchable
            mode="tags"
          />
        </div>
        <div class="tw-w-1/4">
          <div>
            <label class="tw-mr-2 tw-uppercase tw-tracking-wider tw-text-xs">Projects</label>
          </div>
          <MultiSelect
            v-if="projects.length > 0"
            v-model="filter.projects"
            :options="projects"
            placeholder="Alle projecten"
            searchable
            mode="tags"
          />
        </div>
        <div class="tw-w-1/4">
          <label class="tw-mr-2 tw-uppercase tw-tracking-wider tw-text-xs">Address search</label>
          <div>
            <input
              v-model="filter.query"
              class="tw-w-full tw-border tw-border-gray-300 tw-rounded-sm tw-py-1 tw-px-2"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading">Loading...</div>
    <div v-else class="tw-my-2">{{ total }} addresses found</div>

    <div
      v-if="selectedIds.length"
      class="tw-bg-gray-100 tw-rounded tw-p-2 tw-my-2 tw-flex tw-items-center tw-gap-2"
    >
      <div class="tw-font-bold tw-text-sm">
        Voeg een groep toe aan {{ selectedIds.length }} geselecteerde adres(sen):
      </div>
      <div class="tw-w-1/3">
        <MultiSelect
          v-model="batchGroupId"
          :options="batchGroupOptions"
          :disabled="batchWocoConflict"
          :placeholder="batchWocoConflict ? 'Selecteer adressen van één woco' : 'Kies een groep'"
          searchable
        />
        <div v-if="batchWocoConflict" class="tw-text-xs tw-text-red-600 tw-mt-1">
          De geselecteerde adressen horen bij meerdere woco's
          ({{ [...batchWocos].sort().join(', ') }}). Een adres kan niet naar een andere woco,
          dus selecteer adressen van één woco tegelijk.
        </div>
      </div>
      <button
        class="tw-text-xs tw-rounded-sm tw-bg-green-400 tw-text-white tw-p-2 disabled:tw-opacity-30"
        :disabled="!batchGroupId || batchIsSaving || batchWocoConflict"
        @click="applyBatchAdd"
      >
        {{ batchIsSaving ? 'Bezig...' : 'Toevoegen' }}
      </button>
      <button
        class="tw-text-xs tw-rounded-sm tw-bg-red-300 tw-text-white tw-p-1"
        @click="clearSelection"
      >
        Selectie wissen
      </button>
    </div>

    <!-- <div class="tw-bg-gray-100 tw-rounded tw-p-2 tw-flex">
      <div class="tw-font-bold tw-text-sm">Add to or remove group from all addresses in filter ({{ total }})</div>
      <div class="">
        <div class="tw-gap-2 tw-flex">
          <input type="radio" id="add_group" name="action" value="add_group" v-model="action" />
          <label for="add_group">Add group</label>
        </div>
        <div class="tw-gap-2 tw-flex">
          <input type="radio" id="remove_group" name="action" value="remove_group" v-model="action" />
          <label for="remove_group">Remove group</label>
        </div>
      </div>
      <div>
        Group
      </div>
    </div> -->

    <table class="tw-w-full" v-if="addresses" :class="{ 'tw-opacity-50': isFetching }">
      <tr>
        <th class="tw-w-8">
          <input type="checkbox" :checked="allVisibleSelected" @change="toggleSelectAll" />
        </th>
        <th class="tw-w-1/5">Address</th>
        <th class="tw-w-1/5">Project</th>
        <th class="tw-w-3/5">Groups</th>
      </tr>
      <tr v-for="address in addresses" :key="address.id">
        <td class="tw-align-middle">
          <input type="checkbox" :value="address.id" v-model="selectedIds" />
        </td>
        <td class="tw-align-middle">{{ address.address }}</td>
        <td class="tw-align-middle">{{ addressProjects(address).join(', ') }}</td>
        <td>
          <div v-if="groupsInput[address.id]" class="tw-flex tw-items-center tw-gap-2">
            <MultiSelect
              v-model="groupsInput[address.id]"
              :options="optionsForGroups(groupsInput[address.id], address.groups)"
              searchable
              mode="tags"
            />
            <div>
              <!-- Disabled while this address's save is in flight. Without it a save
                   that appears to hang invites more clicks, and every click is another
                   PUT writing the same rows; one incident had 9 concurrent saves for a
                   single address, which the ingress then retried into 27. -->
              <button
                class="tw-h-full tw-text-xs tw-rounded-sm tw-bg-green-400 tw-text-white tw-p-2 disabled:tw-opacity-30"
                @click="saveAdress(address)"
                :disabled="groupsInput[address.id].length === 0 || !!savingIds[address.id]"
              >
                {{ savingIds[address.id] ? 'Bezig...' : 'Save' }}
              </button>
            </div>
            <div>
              <button
                class="tw-text-xs tw-rounded-sm tw-bg-red-300 tw-text-white tw-p-1"
                @click="groupsInput[address.id] = null"
              >
                Cancel
              </button>
            </div>
          </div>

          <div v-else>
            <span
              v-for="group in address.groups"
              :key="group.id"
              class="tw-text-xs tw-rounded-sm tw-bg-blue-50 tw-p-1 tw-mr-1"
            >
              {{ group.name }}
            </span>
            <button
              class="tw-text-xs tw-rounded-sm tw-bg-green-400 tw-text-white tw-p-1 tw-mr-1"
              @click="openEdit(address)"
            >
              Edit
            </button>
          </div>
        </td>
      </tr>
    </table>

    <div class="tw-flex tw-flex-row tw-gap-2 tw-mt-3">
      <button
        :disabled="!hasPrevPage"
        @click="prevPage"
        class="tw-bg-blue-400 tw-rounded tw-p-1 disabled:tw-bg-gray-100"
      >
        Previous
      </button>
      <button
        :disabled="!hasNextPage"
        @click="nextPage"
        class="tw-bg-blue-400 tw-rounded tw-p-1 disabled:tw-bg-gray-100"
      >
        Next
      </button>
    </div>
  </div>
</template>

<script setup>
import { useQuery, useMutation } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import { computed, reactive, ref, watch, unref } from 'vue'
import MultiSelect from '@/components/shared/MultiSelect.vue'

const filter = reactive({
  projects: [],
  groups: [],
  query: '',
})

const groupsInput = ref({})
const error = ref()

// Which addresses have a save in flight. Per address, not one global flag: the
// rows are independent and a save on one must not disable the others.
const savingIds = ref({})

const selectedIds = ref([])
const batchGroupId = ref(null)
const batchIsSaving = ref(false)
const batchSuccess = ref(null)

const limit = 100
const offset = ref(0)

// The batch selection only makes sense within one result page
watch(filter, () => {
  offset.value = 0
  clearSelection()
})
watch(offset, clearSelection)

function clearSelection() {
  selectedIds.value = []
  batchGroupId.value = null
}

const getAddresses = () => {
  const params = new URLSearchParams()
  params.append('limit', limit)
  params.append('offset', offset.value)
  if (filter.projects.length > 0) {
    for (const project of filter.projects) {
      params.append('projects', project)
    }
  }
  if (filter.groups.length > 0) {
    for (const group of filter.groups) {
      params.append('group_ids', group)
    }
  }
  if (filter.query) {
    params.append('query', filter.query)
  }

  return fetchData(`/groups/api/v1/address?${params}`)
}
const addressQuery = useQuery({
  queryKey: computed(() => ['addresses', filter, offset.value]),
  queryFn: getAddresses,
  keepPreviousData: true,
})

const getGroups = () => fetchData(`/groups/api/v1/group`)

const groupQuery = useQuery({
  queryKey: ['groups'],
  queryFn: getGroups,
})

const groups = computed(() => (groupQuery.data.value && groupQuery.data.value.data) || [])
const projects = computed(() => Array.from(new Set(groups.value.map((group) => group.project))))

const addresses = computed(() => {
  const allGroups = groups.value
  const list = addressQuery.data.value && addressQuery.data.value.data
  if (!list) return []

  return list.map((address) => {
    const addressGroups = allGroups.filter((group) => address.group_ids.includes(group.id))

    return {
      ...address,
      groups: addressGroups,
    }
  })
})

const total = computed(() => addressQuery.data.value && addressQuery.data.value.total)

function openEdit(address) {
  groupsInput.value = {
    ...groupsInput.value,
    [address.id]: address.groups.map((g) => g.id),
  }
}

const { mutate: saveAdress } = useMutation({
  mutationFn: (address) => {
    const payload = {
      group_ids: groupsInput.value[address.id],
    }
    return fetchData(`/groups/api/v1/address/${address.id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  },
  onMutate: (address) => {
    savingIds.value = { ...savingIds.value, [address.id]: true }
  },
  onSettled: (data, e, address) => {
    const { [address.id]: _done, ...rest } = savingIds.value
    savingIds.value = rest
  },
  onError: (e) => {
    if (e.message) {
      error.value = e.message
    } else {
      error.value = e
    }
  },
  onSuccess: (data, address) => {
    error.value = null
    groupsInput.value = {
      ...groupsInput.value,
      [address.id]: undefined,
    }
    addressQuery.refetch()
  },
})

const isLoading = computed(() => addressQuery.isLoading.value || groupQuery.isLoading.value)
const isFetching = computed(() => addressQuery.isFetching.value)

function addressProjects(address) {
  return [...new Set(address.groups.map((g) => g.project))]
}

function optionsForGroups(formGroups, addressGroups) {
  const form = unref(formGroups)

  // An address may span multiple projects: offer all groups of the address's
  // woco, no longer locked to the first selected group's project. The backend
  // validates the resulting combination (component overlap, woco).
  const woco = addressGroups[0]?.woco ?? groups.value.find((g) => g.id === form[0])?.woco

  return groups.value
    .filter((g) => !woco || g.woco === woco)
    .map((g) => ({
      value: g.id,
      label: `${g.project} / ${g.name}`,
    }))
}

const selectedAddresses = computed(() =>
  addresses.value.filter((address) => selectedIds.value.includes(address.id))
)

const allVisibleSelected = computed(
  () =>
    addresses.value.length > 0 &&
    addresses.value.every((address) => selectedIds.value.includes(address.id))
)

function toggleSelectAll() {
  selectedIds.value = allVisibleSelected.value ? [] : addresses.value.map((address) => address.id)
}

const batchWocos = computed(
  () =>
    new Set(
      selectedAddresses.value.flatMap((address) => address.groups.map((g) => g.woco)).filter(Boolean)
    )
)

const batchWocoConflict = computed(() => batchWocos.value.size > 1)

const batchGroupOptions = computed(() => {
  // An address never moves between woco's, so a mixed selection has no valid group
  if (batchWocoConflict.value) return []

  const pool = batchWocos.value.size === 1
    ? groups.value.filter((g) => g.woco === [...batchWocos.value][0])
    : groups.value
  return pool.map((g) => ({ value: g.id, label: `${g.project} / ${g.name}` }))
})

async function applyBatchAdd() {
  batchIsSaving.value = true
  error.value = null
  batchSuccess.value = null

  // Sequential on purpose: every save can trigger task initialization in the
  // groups service, and a per-address failure must not abort the rest
  const failures = []
  let succeeded = 0
  for (const address of selectedAddresses.value) {
    const groupIds = [...new Set([...address.group_ids, batchGroupId.value])]
    try {
      await fetchData(`/groups/api/v1/address/${address.id}`, {
        method: 'PUT',
        body: JSON.stringify({ group_ids: groupIds }),
      })
      succeeded += 1
    } catch (e) {
      // fetchData throws parsed JSON (no .message) for API error bodies
      const reason = e.message || e.detail || JSON.stringify(e)
      failures.push(`${address.address}: ${reason}`)
    }
  }

  batchIsSaving.value = false
  if (failures.length) {
    error.value = `${succeeded} adres(sen) bijgewerkt, ${failures.length} mislukt:\n${failures.join(
      '\n'
    )}`
  } else {
    batchSuccess.value = `${succeeded} adres(sen) toegevoegd aan de groep`
    clearSelection()
  }
  addressQuery.refetch()
}

const nextPage = () => (offset.value += limit)
const hasNextPage = computed(() => total.value > offset.value + limit)

const prevPage = () => (offset.value -= limit)
const hasPrevPage = computed(() => offset.value > 0)
</script>
